import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EditLocationModel } from "../../../core/api/locations/types";
import useUser from "../../../core/user/useUser";
import { getLocation } from "../../../core/api/locations/locations";
import { EditLocationActions } from "../../../components/location/LocationEditForm";
import { notifyApiErrors } from "../../../core/helpers/helpers";

export const SharedLocationContext = createContext<{
  location?: EditLocationModel;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isError: boolean;
  fetchLocation: () => Promise<void>;
  formRef: EditLocationActions | null;
  setFormRef: (ref: EditLocationActions | null) => void;
}>({
  location: undefined,
  isLoading: false,
  setIsLoading: () => {},
  isError: false,
  fetchLocation: async () => {},
  formRef: null,
  setFormRef: () => {},
});

export const SharedLocationProvider = ({
  locationId,
  children,
}: {
  locationId: string;
  children: ReactNode;
}) => {
  const { authenticatedRequest } = useUser();
  const isAlive = useRef(false);
  const [isError, setIsError] = useState(false);

  const [location, setLocation] = useState<EditLocationModel>();
  const [isLoading, setIsLoading] = useState(false);
  const [formRef, setFormRef] = useState<EditLocationActions | null>(null);

  const fetchLocation = async () => {
    setIsError(false);
    setIsLoading(true);

    try {
      const result = await getLocation(locationId, authenticatedRequest);
      if (isAlive.current) {
        setLocation(result.data);
      }
    } catch (error: any) {
      if (isAlive.current) {
        setIsError(true);
        notifyApiErrors(error.response?.data?.errors);
      }
    }
    if (isAlive.current) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLocation();
  }, [locationId]);

  useEffect(() => {
    isAlive.current = true;
    return () => {
      isAlive.current = false;
    };
  }, []);

  const contextData = useMemo(
    () => ({
      location,
      isLoading,
      setIsLoading,
      isError,
      fetchLocation,
      formRef,
      setFormRef,
    }),
    [location, isLoading, isError, formRef],
  );

  return (
    <SharedLocationContext.Provider value={contextData}>
      {children}
    </SharedLocationContext.Provider>
  );
};
